import { useState } from "react";
import { Share2, Copy, Check, Loader2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ShareToggle({ campaign, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const enabled = Boolean(campaign?.public_share_enabled);
  const shareUrl = campaign?.public_share_url;

  const toggle = async (next) => {
    setBusy(true);
    try {
      const url = next
        ? `/campaigns/${campaign.id}/share/enable`
        : `/campaigns/${campaign.id}/share/disable`;
      await api.post(url);
      toast.success(next ? "Public share link enabled" : "Public share link disabled");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!shareUrl) return;
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Share link copied");
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8" data-testid="share-panel">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <Share2 className="w-5 h-5 text-slate-500" />
          <h2 className="text-lg font-bold text-slate-900">Public share link</h2>
        </div>
        <div className="flex items-center gap-2">
          {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
          <Switch
            checked={enabled}
            onCheckedChange={toggle}
            disabled={busy}
            data-testid="share-toggle"
          />
        </div>
      </div>
      <p className="text-sm text-slate-500 mt-2">
        Share a read-only stats page for this campaign — no login needed. Great for showing clients live results.
      </p>

      {enabled && shareUrl && (
        <div className="mt-4 space-y-3" data-testid="share-active">
          <div className="flex flex-wrap items-center gap-2">
            <code className="font-mono text-sm text-slate-900 bg-slate-100 px-3 py-2 rounded-lg break-all flex-1 min-w-0" data-testid="share-url-code">
              {shareUrl}
            </code>
            <button
              onClick={copy}
              data-testid="share-copy-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
            >
              {copied ? (
                <>
                  <Check className="w-3.5 h-3.5 text-emerald-600" /> Copied
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5" /> Copy
                </>
              )}
            </button>
            <a href={shareUrl} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm" className="rounded-lg" data-testid="share-open-btn">
                <ExternalLink className="w-3.5 h-3.5 mr-1" />
                Open
              </Button>
            </a>
          </div>
          <p className="text-xs text-slate-500">
            The public page shows total scans, weekly count, a 30-day chart, and a device breakdown. It does not reveal the destination URL, IPs, or geo detail.
          </p>
        </div>
      )}
    </div>
  );
}
